import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CRow,
  CSpinner,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilArrowLeft, cilCreditCard } from "@coreui/icons";
import {
  canExtendYear,
  canResumeSubscription,
  canUpgradeToYearly,
  type CustomerTradingStrategySubscriptionDto,
  deriveSubscriptionDisplayStatus,
  type SubscriptionDisplayStatus,
  subscriptionStatusColor,
  isActiveSubscription,
  isStaffManaged,
  useMySubscriptions,
} from "../../api/hooks/useStrategySubscriptions";
import { formatDateShort, formatDayMonth } from "../../lib/dates";
import { useCartStore } from "../../state/cartStore";
import { formatPriceUsd } from "../../lib/money";
import { savingOnLine } from "../../lib/offer-savings";
import {
  offersForLine,
  usableOffers,
  useUsableOffers,
} from "../../api/hooks/useOffers";
import { useTradingStrategies } from "../../api/hooks/useTradingStrategies";
import { routePaths } from "../../routes/route-paths";
import {
  StrategyFigures,
  TradingStrategyInsights,
} from "./components/TradingStrategyInsights";
import { StrategyColumnLinks } from "./components/StrategyColumnLink";
import { StrategyFeedbackLinks } from "./components/StrategyFeedbackLinks";
import { TradingViewScriptLink } from "./components/TradingViewScriptLink";
import { StrategyTraitFacts } from "./components/StrategyTraitFacts";
import { StrategyPriceHeadline } from "./components/StrategyPrice";
import { StrategySideTabs } from "./components/StrategySideTabs";
import { sidesOf } from "./strategySides";
import { UpgradeOnItsOwnDialog } from "./components/UpgradeOnItsOwnDialog";
import { ExtendOnItsOwnDialog } from "./components/ExtendOnItsOwnDialog";
import { UpdateCardButton } from "../../components/common/UpdateCardButton";

export function SubscriptionDetailCoreui() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { strategyId = "" } = useParams<{ strategyId: string }>();
  const { data: subscriptions = [], isLoading, error } = useMySubscriptions();
  const { data: catalogue = [] } = useTradingStrategies();
  const strategyItems = useCartStore((s) => s.strategyItems);
  const membershipItems = useCartStore((s) => s.membershipItems);
  const addOnItems = useCartStore((s) => s.addOnItems);
  const cartUpgrade = useCartStore((s) => s.upgrade);
  const startUpgrade = useCartStore((s) => s.startUpgrade);
  const cartExtension = useCartStore((s) => s.extension);
  const startExtension = useCartStore((s) => s.startExtension);
  const offers = usableOffers(useUsableOffers().data);
  const [askingToEmptyCart, setAskingToEmptyCart] = useState(false);
  const [askingToEmptyCartForExtension, setAskingToEmptyCartForExtension] = useState(false);

  // If a customer has multiple subscription rows for the same strategy (e.g.
  // they subscribed, ended, then re-subscribed), the hero band shows the most
  // recent one and the history remains available through the filters list.
  const subscriptionsForStrategy = useMemo(
    () =>
      [...subscriptions]
        .filter((s) => s.strategy.id === strategyId)
        .sort((a, b) => +new Date(b.activeFrom) - +new Date(a.activeFrom)),
    [subscriptions, strategyId],
  );
  const subscription = subscriptionsForStrategy[0];

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <CSpinner color="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <CAlert color="danger">
          Unable to load your subscription. Please try again.
        </CAlert>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div>
        <CAlert color="warning">
          Subscription not found.{" "}
          <Link to={routePaths.myStrategies}>Back to my strategies</Link>.
        </CAlert>
      </div>
    );
  }

  const strategy = subscription.strategy;
  const sides = sidesOf(strategy, catalogue);
  const hasSideTabs = sides.length > 1;
  const displayStatus = deriveSubscriptionDisplayStatus(subscription);
  // A staff-managed subscription is managed entirely from the Back Office — the customer is not
  // offered any lifecycle action on it.
  const staffManaged = isStaffManaged(subscription);
  const canEnd = !staffManaged && displayStatus === "Active";
  const canResume = !staffManaged && canResumeSubscription(subscription);
  const canUpgrade = canUpgradeToYearly(subscription);
  const canExtend = canExtendYear(subscription);
  const bestYearlyOffer =
    offersForLine(offers, strategy.id, "Yearly").sort(
      (a, b) =>
        savingOnLine(b, strategy.yearlyPriceUsd) -
        savingOnLine(a, strategy.yearlyPriceUsd)
    )[0] ?? null;
  const firstYearUsd =
    strategy.yearlyPriceUsd -
    (bestYearlyOffer ? savingOnLine(bestYearlyOffer, strategy.yearlyPriceUsd) : 0);
  // Against what she pays now, so an old, lower monthly price shows no saving rather than a false one.
  const yearlySaving =
    (subscription.priceAtSubscriptionUsd ?? 0) * 12 - firstYearUsd;

  const upgrade = () => {
    startUpgrade({
      strategyId: strategy.id,
      name: strategy.publicName,
      yearlyPriceUsd: strategy.yearlyPriceUsd,
      // The offer the button's saving counts, so the cart opens at the price the button promised.
      offerId: bestYearlyOffer?.id ?? null,
    });
    navigate(routePaths.cart);
  };

  const pendingSwitch = subscription.pendingSwitch;
  const firstPaymentFailed = pendingSwitch?.firstPaymentFailed ?? false;
  const paymentFailed = subscription.paymentFailed;
  const statusDate = subscriptionStatusDate(subscription, displayStatus, pendingSwitch !== null || paymentFailed);

  const handleUpgrade = () => {
    const alreadyInCart = cartUpgrade?.strategyId === strategy.id;
    if (alreadyInCart) {
      navigate(routePaths.cart);
      return;
    }

    const cartIsEmpty =
      strategyItems.length === 0 &&
      membershipItems.length === 0 &&
      addOnItems.length === 0 &&
      cartUpgrade === null &&
      cartExtension === null;
    if (!cartIsEmpty) {
      setAskingToEmptyCart(true);
      return;
    }

    upgrade();
  };

  const extend = () => {
    startExtension({
      strategyId: strategy.id,
      name: strategy.publicName,
      yearlyPriceUsd: subscription.priceAtSubscriptionUsd ?? strategy.yearlyPriceUsd,
      offerId: bestYearlyOffer?.id ?? null,
    });
    navigate(routePaths.cart);
  };

  const handleExtend = () => {
    if (cartExtension?.strategyId === strategy.id) {
      navigate(routePaths.cart);
      return;
    }

    const cartIsEmpty =
      strategyItems.length === 0 &&
      membershipItems.length === 0 &&
      addOnItems.length === 0 &&
      cartUpgrade === null &&
      cartExtension === null;
    if (!cartIsEmpty) {
      setAskingToEmptyCartForExtension(true);
      return;
    }

    extend();
  };

  return (
    <div>
      {/* Billing is per-customer, not per-subscription - one card and one invoice for the whole
          bundle - so the page only points at it, level with the tabs. On a phone it sits above them,
          so the tabs stay joined to the card. */}
      <div className="d-flex flex-column-reverse flex-sm-row align-items-sm-end justify-content-between gap-2">
        <StrategySideTabs
          sides={sides}
          currentId={strategy.id}
          cycle={subscription.billingCycle ?? "Monthly"}
          browsing={false}
        />
        {!staffManaged && (
          <CButton
            color="primary"
            className="align-self-end ms-sm-auto mb-sm-2 flex-shrink-0"
            onClick={() => navigate(routePaths.billing)}
          >
            <CIcon icon={cilCreditCard} className="me-2" />
            Billing
          </CButton>
        )}
      </div>
      <CCard style={hasSideTabs ? { borderTopLeftRadius: 0 } : undefined}>
        <CCardBody>
          {/* The strategy on the left, the customer's subscription on the right, so nothing about the
              subscription is spread between the pills, the facts and a panel. md-breakpoint split so
              wider phones in landscape get the side-by-side layout instead of stacking. */}
          <CRow xs={{ gutter: 4 }} className="pb-4 border-bottom">
            <CCol md={8}>
              <h2 className="mb-2">{strategy.publicName}</h2>
              <p
                className="text-body-secondary mb-0"
                style={{ fontSize: "1rem" }}
              >
                {strategy.description}
              </p>

              {/* Wrapping only where it has to - see the browse detail page. The side tabs already
                  name the side, so Direction only shows without them; timeframe and asset class
                  are facts here rather than pills above the name. */}
              <div className="mt-4 pt-4 border-top strategy-facts">
                <StrategyTraitFacts
                  strategy={strategy}
                  showDirection={!hasSideTabs}
                  showMarket
                />
              </div>

              {/* Beside the subscription column rather than across the page, so the first screen
                  holds the strategy, its results and the subscription without a gap under the facts. */}
              <div className="mt-4">
                <StrategyFigures strategy={strategy} threeAcross />
              </div>
            </CCol>

            <CCol md={4}>
              <div className="subscription-column d-flex flex-column gap-3">
                <div
                  className="text-body-secondary text-uppercase small fw-semibold"
                  style={{ letterSpacing: "0.08em" }}
                >
                  Your subscription
                </div>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <CBadge
                    color={subscriptionStatusColor(displayStatus)}
                    shape="rounded-pill"
                  >
                    {displayStatus}
                  </CBadge>
                  {(firstPaymentFailed || paymentFailed) && (
                    <CBadge color="danger" shape="rounded-pill">
                      Payment failed
                    </CBadge>
                  )}
                  {pendingSwitch && !firstPaymentFailed && (
                    <CBadge color="info" shape="rounded-pill">
                      Yearly from {formatDayMonth(pendingSwitch.switchDate)}
                    </CBadge>
                  )}
                  {statusDate && (
                    <span className="small text-body-secondary">{statusDate}</span>
                  )}
                </div>
                <div style={{ fontSize: "2rem", fontWeight: 700, lineHeight: 1.1 }}>
                  <StrategyPriceHeadline
                    free={subscription.priceAtSubscriptionUsd === 0}
                    amountUsd={subscription.priceAtSubscriptionUsd}
                    cycle={subscription.billingCycle}
                  />
                </div>
                <div className="d-grid gap-2">
                  {canUpgrade && (
                    <>
                      <CButton color="primary" onClick={handleUpgrade}>
                        Upgrade to yearly
                        {yearlySaving > 0 && ` · save ${formatPriceUsd(yearlySaving)}`}
                      </CButton>
                      {bestYearlyOffer && (
                        <div className="small text-body-secondary text-center">
                          with {bestYearlyOffer.publicName}
                        </div>
                      )}
                    </>
                  )}
                  {canExtend && (
                    <>
                      <CButton color="primary" onClick={handleExtend}>
                        Extend your year
                      </CButton>
                      {bestYearlyOffer && (
                        <div className="small text-body-secondary text-center">
                          with {bestYearlyOffer.publicName}
                        </div>
                      )}
                    </>
                  )}
                  {staffManaged ? null : canEnd ? (
                    <CButton
                      color="danger"
                      variant="outline"
                      onClick={() => navigate(`/subscriptions/end/${strategy.id}`)}
                    >
                      End subscription
                    </CButton>
                  ) : canResume ? (
                    <CButton
                      color="success"
                      onClick={() =>
                        navigate(`/subscriptions/resume/${strategy.id}`)
                      }
                    >
                      Resume subscription
                    </CButton>
                  ) : (
                    <CButton color="secondary" disabled>
                      Subscription {displayStatus.toLowerCase()}
                    </CButton>
                  )}
                </div>
                <StrategyColumnLinks>
                  {isActiveSubscription(subscription) && (
                    <TradingViewScriptLink
                      strategy={strategy}
                      returnTo={`/subscriptions/${strategy.id}`}
                    />
                  )}
                  {/* Someone who subscribed comes here, not to the browse page, so the invitation has
                      to be here too - otherwise the people best placed to rate a strategy never see it. */}
                  <StrategyFeedbackLinks strategy={strategy} hasSubscribed />
                </StrategyColumnLinks>
              </div>
            </CCol>
          </CRow>

          {pendingSwitch && firstPaymentFailed && (
            <div className="tinted-danger rounded p-3 mt-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
              <span style={{ flex: "1 1 280px", minWidth: 0 }}>
                <strong>Your first yearly payment didn't go through.</strong> We couldn't take it on{" "}
                {formatDateShort(pendingSwitch.switchDate)}. Update your card and Stripe will try again. If
                it still can't be taken, {strategy.publicName} will end.
              </span>
              <UpdateCardButton />
            </div>
          )}

          {paymentFailed && subscription.renewsOn && (
            <div className="tinted-danger rounded p-3 mt-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
              <span style={{ flex: "1 1 280px", minWidth: 0 }}>
                <strong>
                  Your payment due {formatDateShort(subscription.renewsOn)} didn't go through.
                </strong>{" "}
                {subscription.billingCycle === "Monthly" &&
                  `Your monthly bill includes ${strategy.publicName}. `}
                Update your card and Stripe will try again. If it still can't be taken,{" "}
                {strategy.publicName} will end.
              </span>
              <UpdateCardButton />
            </div>
          )}

          {pendingSwitch && !firstPaymentFailed && (
            <div className="tinted-info rounded p-3 mt-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
              <span>
                <strong>
                  Upgrading to yearly on {formatDateShort(pendingSwitch.switchDate)}.
                </strong>{" "}
                Until then you can stay monthly, and nothing changes.
              </span>
              <CButton
                color="primary"
                variant="outline"
                size="sm"
                onClick={() => navigate(`/subscriptions/stay-monthly/${strategy.id}`)}
              >
                Stay monthly
              </CButton>
            </div>
          )}

          {/* Insights come from the FO strategy row that is attached to the subscription. */}
          <TradingStrategyInsights strategy={strategy} periodInNote showFigures={false} />

          <div className="d-flex gap-2 mt-5 pt-3">
            <CButton
              color="secondary"
              onClick={() => navigate(routePaths.myStrategies)}
            >
              <CIcon icon={cilArrowLeft} className="me-1" />
              {t("common:buttons.backToMyStrategies")}
            </CButton>
          </div>
        </CCardBody>
      </CCard>

      <ExtendOnItsOwnDialog
        strategyName={strategy.publicName}
        visible={askingToEmptyCartForExtension}
        onKeep={() => setAskingToEmptyCartForExtension(false)}
        onEmpty={() => {
          setAskingToEmptyCartForExtension(false);
          extend();
        }}
      />
      <UpgradeOnItsOwnDialog
        strategyName={strategy.publicName}
        visible={askingToEmptyCart}
        onKeep={() => setAskingToEmptyCart(false)}
        onEmpty={() => {
          setAskingToEmptyCart(false);
          upgrade();
        }}
      />
    </div>
  );
}

/**
 * The one date that goes with the status: when it renews, when access stops or stopped, or when it started
 * where it won't simply renew. Not the renewal while an upgrade waits or a bill is unpaid: the band below says
 * what happens instead.
 */
function subscriptionStatusDate(
  subscription: CustomerTradingStrategySubscriptionDto,
  displayStatus: SubscriptionDisplayStatus,
  renewalInDoubt: boolean,
): string | null {
  if (displayStatus === "Active") {
    if (subscription.renewsOn && !renewalInDoubt) {
      return `Renews ${formatDateShort(subscription.renewsOn)}`;
    }
    return `Since ${formatDateShort(subscription.activeFrom)}`;
  }
  if (!subscription.activeTo) {
    return null;
  }
  if (displayStatus === "Cancelled") {
    return `Access until ${formatDateShort(subscription.activeTo)}`;
  }
  return `Ended ${formatDateShort(subscription.activeTo)}`;
}
